import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {CartegraphConfiguration, CartegraphSyncTask} from '../../../../../shared/models/cartegraph.model';
import {CartegraphManagementService} from '../../../../../data/cartegraph/cartegraph-management.service';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {
  MatCell, MatCellDef,
  MatColumnDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {MatSort, MatSortHeader, Sort, SortDirection} from '@angular/material/sort';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ToastService} from '../../../../../shared/services/toast.service';
import {environment} from '../../../../../../environments/environment';
import {CgTaskStatusUpdateDialog} from './cartegraph-tasks-status.copmponent';
import {MainRoute, RootRoute} from '../../../../../shared/models/angular-routing';
import {InsightsRoute} from '../../../../insights/insights-routing.module';
import {firstValueFrom} from 'rxjs';
import {JsonApiResponse} from '../../../../../shared/models/JsonApiResponse';
import {Page} from '../../../../../shared/models/Page';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {CardComponent} from '../../../../../shared/components/card/card.component';
import {CartegraphHeaderComponent} from '../cartegraph-header/cartegraph-header.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {MatSelect} from '@angular/material/select';
import {MatOption} from '@angular/material/select';
import {RouterLink} from '@angular/router';
import {DatePipe, NgTemplateOutlet} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatBadge} from '@angular/material/badge';
import {CardContentDirective} from '../../../../../shared/components/card/card-content.directive';
import {MatButton, MatIconButton} from '@angular/material/button';

@Component({
  selector: 'app-manage-cartegraph-tasks',
  templateUrl: './cartegraph-tasks.component.html',
  styleUrls: ['./cartegraph-tasks.component.scss',
    '../../../settings-fields.scss',
    '../../../settings-common.scss'
  ],
  imports: [
    CardComponent,
    CartegraphHeaderComponent,
    MatProgressSpinner,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatPaginator,
    MatTable,
    MatSort,
    RouterLink,
    DatePipe,
    MatIcon,
    MatBadge,
    CardContentDirective,
    NgTemplateOutlet,
    MatInput,
    MatButton,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatCellDef,
    MatHeaderCellDef,
    MatHeaderRowDef,
    MatRowDef,
    MatIconButton,
    MatSortHeader
  ],
  standalone: true
})
export class CartegraphTasksComponent implements OnInit {

  configuration: CartegraphConfiguration;

  isInitializing = false;
  isLoading = false;
  uiError: string;

  displayedColumns: string[] = [
    'id', 'taskType', 'created', 'shift', 'cgIdField', 'action'];
  processingStatuses: { id: string, label: string }[] = [
    {id: 'WAITING', label: 'Wait'},
    {id: 'SUCCESS', label: 'Success'},
    {id: 'FAIL', label: 'Fail'},
    {id: 'NONE', label: 'None'}];
  tasks: MatTableDataSource<CartegraphSyncTask> = new MatTableDataSource<CartegraphSyncTask>([]);

  sortActive = 'created';
  sortDirection: SortDirection = 'desc';

  // used in *ngIf, requires to be loaded dynamically
  @ViewChild(MatSort, {static: false}) set sorter(sort: MatSort) {
    if (sort) {
      this.tasks.sort = sort;
    }
  }

  // used out of *ngIf can be injected
  @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;
  itemCount = 0;
  pageSize = 20;
  pageIndex = 0;
  form: FormGroup;

  constructor(
    private cartegraphService: CartegraphManagementService,
    private dialog: MatDialog,
    private toast: ToastService,
    private readonly fb: FormBuilder,
  ) {
    this.tasks.paginator = this.paginator;
    this.form = fb.group({
      searchText: fb.control(null),
      processingStatus: fb.control(this.processingStatuses),
    });
  }

  ngOnInit() {
    this.isInitializing = true;

    Promise.all([
      this.cartegraphService.getConfiguration(),
      this.getTasksPromise()
    ]).then(results => {
      const [configResponse, tasksResponse] = results;

      if (configResponse.error) {
        this.uiError = configResponse.error;
      } else {
        this.configuration = configResponse.data ? configResponse.data : new CartegraphConfiguration();
      }
      // process tasks
      this.initTasks(tasksResponse);
    }).catch((error) => {
      console.log(error);
      this.uiError = error;
    }).finally(() => {
      this.isInitializing = false;
    });

  }

  private getTasksPromise(page: number = 0) {
    const sortField = this.tasks?.sort?.active ? `${this.tasks?.sort?.active},${this.tasks?.sort?.direction}`
      : `${this.sortActive},${this.sortDirection}`;
    const searchText = this.form.controls['searchText'].value;
    const processingStatus = this.form.controls['processingStatus'].value?.map(s => s.id).join(',');
    return this.cartegraphService.getTaskList(page, this.pageSize, processingStatus, searchText, sortField);
  }

  private initTasks(tasksResponse: JsonApiResponse<Page<CartegraphSyncTask>>) {
    if (tasksResponse.error) {
      this.uiError = tasksResponse.error;
    } else {
      this.tasks.data = tasksResponse.data.content;
      this.pageSize = tasksResponse.data?.page?.size;
      this.pageIndex = tasksResponse.data?.page?.number;
      this.itemCount = tasksResponse.data?.page?.totalElements;
    }
  }

  getTasks(page: number = 0) {
    this.isLoading = true;
    this.uiError = null;
    this.form.disable();

    this.getTasksPromise(page)
      .then(tasksResponse => {
        if (tasksResponse.error) {
          this.uiError = tasksResponse.error;
        } else {
          this.initTasks(tasksResponse);
        }
      }).catch(error => {
      console.log(error);
      this.uiError = error;
    }).finally(() => {
      this.isLoading = false;
      this.form.enable();
    });
  }

  reprocessSyncTask(syncTask: CartegraphSyncTask) {
    firstValueFrom(this.cartegraphService.updateSyncTask(syncTask.id, 'reprocess')
    ).then(response => {
      this.getTasks();
      this.toast.long(response.data);
    })
      .catch(error => {
        console.log(error);
        this.uiError = error;
      });
  }

  onPage(pageEvent: PageEvent) {
    // decide to reset sorting here
    this.getTasks(pageEvent.pageIndex);
  }

  cgDetailLink(cgOid: string) {
    // this must be based on OID not cgField
    return `${this.configuration.baseUrl}/#FlexUI/Edit/cgTasksClass/${cgOid}`;
  }

  shiftLink(shiftId: number): string[] {
    return [
      environment.base_href,
      RootRoute.MAIN,
      MainRoute.INSIGHTS,
      InsightsRoute.SHIFT_REPORT,
      InsightsRoute.SHIFT_DETAIL,
      `${shiftId}`
    ];
  }

  doSearch() {
    this.getTasks(this.pageIndex);
  }

  clearFilters() {
    this.form.reset();
    this.form.controls['processingStatus'].setValue(this.processingStatuses);
    this.getTasks();
  }

  sortChange(sort: Sort) {
    this.sortActive = sort.active;
    this.sortDirection = sort.direction;
    this.getTasks();
  }

  messageDetail(task: CartegraphSyncTask) {
    if (task.processMessage?.trim().length > 0) {
      this.dialog.open(CartegraphTasksDetailDialogComponent, {
        width: '800px',
        height: '800px',
        data: task,
      });
    }
  }

  updateStatus(task: CartegraphSyncTask) {
    const dialogRef = this.dialog.open(CgTaskStatusUpdateDialog, {
      data: Object.assign({}, task),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        firstValueFrom(this.cartegraphService.updateSyncTask(result.id, 'update', result)
        ).then(response => {
          this.getTasks();
          this.toast.long(response.data);
        })
          .catch(error => {
            console.log(error);
            this.uiError = error;
          });
      }
    });
  }


}

@Component({
  selector: 'app-manage-cartegraph-task-detail',
  template: `<h1 mat-dialog-title>Processing detail</h1>
  <mat-dialog-content class="dialog">
      <div>ID: {{ data.id }}</div>
      <div>Shift ID: {{ data.shiftId }}</div>
      <div>Created: {{ data.created | date: 'medium' }}</div>
      <div>Type: {{ data.taskType }}</div>
      <div>Retry attempt count: {{ data.retryCount }}</div>
      <h2>Stacktrace</h2>
      {{ data.processMessage }}
  </mat-dialog-content>
  <mat-dialog-actions>
      <button mat-button mat-dialog-close color="accent">Close</button>
  </mat-dialog-actions>`,
  standalone: false
})
export class CartegraphTasksDetailDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<CartegraphTasksDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CartegraphSyncTask,
  ) {
  }
}
